<!-- player/bookings/BookingNew/views/BookingNewView.vue -->
<script lang="ts" setup>
import { ref, watch, computed, nextTick, onMounted } from 'vue'
import { useRouter, useRoute, onBeforeRouteUpdate } from 'vue-router'
import { useHead } from '@vueuse/head'

import { useClient } from '@base/api/useClient'

import USButton from '@base/components/USButton/USButton.vue'
import USInputSelect from '@base/components/USInputSelect/USInputSelect.vue'

import BookingAttendeesForm from '@player/bookings/BookingNew/components/BookingAttendeesForm.vue'
import InformationCardEventTeeTime from '@player/_components/InformationCardEventTeeTime/InformationCardEventTeeTime.vue'
import InformationCardOrderTotal from '@player/_components/InformationCardOrderTotal/InformationCardOrderTotal.vue'
import InformationCardPlayers from '@player/_components/InformationCardPlayers/InformationCardPlayers.vue'
import SplitContent from '@player/_components/SplitContent/SplitContent.vue'

import { EventTeeTime as EventTeeTimeType } from '@player/_components/InformationCardEventTeeTime/EventTeeTimeType'

import { BookingTeeTimeType, BookingType } from '__generated__/globalTypes'
import { formatDate } from '@base/helpers/formatDate'
import { GET_BOOKING_EVENT_QUERY } from '../api/queries/getBookingEvent'
import { GET_BOOKING_SAVED_CARDS_QUERY } from '../api/queries/getBookingSavedCards'
import { GET_BOOKING_TEE_TIME_QUERY } from '../api/queries/getBookingTeeTime'
import {
  getBookingEvent,
  getBookingEvent_getEvent_GetEventOutput_event as GqlBookingEvent
} from '__generated__/getBookingEvent'
import {
  getBookingSavedCards,
  getBookingSavedCards_getSavedCards_GetSavedCardsOutput_savedCards as GqlSavedCard
} from '__generated__/getBookingSavedCards'
import {
  getBookingTeeTime,
  getBookingTeeTime_GetTeeTime_GetTeeTimeOutput_teeTime as GqlBookingTeeTime
} from '__generated__/getBookingTeeTime'
import { playerTitle } from '@player/_lib/playerTitle'
import { props as propDefinition } from './props'
import { newBooking } from '__generated__/newBooking'
import { newGuestBooking } from '__generated__/newGuestBooking'
import { stripeElements } from '@base/lib/stripeElements'
import { useToastsStore } from '@base/store/toastsStore'
import { useUserStore } from '@base/store/userStore'
import { NEW_BOOKING_MUTATION } from '../api/mutations/newBooking'
import { NEW_GUEST_BOOKING_MUTATION } from '../api/mutations/newGuestBooking'
import { ToastSemantic } from '@base/_types/Toast'
import { NEW_SETUP_INTENT_MUTATION } from '@player/account/SavedPayments/api/mutations/newSetupIntent'
import { NEW_GUEST_SETUP_INTENT_MUTATION } from '@player/account/SavedPayments/api/mutations/newGuestSetupIntent'
import { newSetupIntent } from '__generated__/newSetupIntent'
import { newGuestSetupIntent } from '__generated__/newGuestSetupIntent'
import { useViewRequest } from '@base/lib/viewHelpers/useViewRequest'
import { useViewState } from '@base/lib/viewHelpers/useViewState'
import { usersRouteNames } from '@player/users/router/routeNames'

// Props
const props = defineProps(propDefinition)

// Static Variables
const client = useClient()
const router = useRouter()
const route = useRoute()
const toastsStore = useToastsStore()
const user = useUserStore()

useHead({
  title: playerTitle('New Booking')
})

// NEW

type BookingAdmission = {
  id: string
  fee9?: number | null
  fee18?: number | null
  price?: number | null
  admissionTypeId: string
  admissionTypeName: string
  admissionTypePosition: number
  attendees: number
}

type CustomAttendeeField = {
  id: string
  label: string
  type: 'text' | 'number'
  required: boolean
  placeholder?: string | null
}

type BookingAttendee = {
  id: string
  firstName?: string
  lastName?: string
  email: string
  customFields?: { [fieldId: string]: string }
}

type BookingAttendeeFormatted = {
  golfEventAdmissionId?: string
  teeTimeAdmissionId?: string
  firstName?: string
  lastName?: string
  email: string
  customFields?: string // JSON string of { [fieldId: string]: string }
}

// Inputs
enum NewBookingStep {
  Attendees = 'Players Information',
  Payment = 'Payment Details',
  ConfirmBooking = 'Confirm Booking'
}
const currentStep = ref<NewBookingStep>(NewBookingStep.Attendees)
const previousStep = (currentStep: NewBookingStep): NewBookingStep | null => {
  if (currentStep === NewBookingStep.Payment) return NewBookingStep.Attendees
  else if (currentStep === NewBookingStep.ConfirmBooking) return NewBookingStep.Payment
  return null
}
const nextStep = (currentStep: NewBookingStep): NewBookingStep | null => {
  if (currentStep === NewBookingStep.Attendees) return NewBookingStep.Payment
  else if (currentStep === NewBookingStep.Payment) return NewBookingStep.ConfirmBooking
  return null
}
const type = ref(route.query.type?.toString())
const id = ref(route.query.id?.toString())
const teeTimeType = ref(route.query['tee-time-type']?.toString())

const bookingType = computed(() => {
  if (type.value === 'tee-time') return BookingType.TEE_TIME
  return BookingType.EVENT
})
const bookingTeeTimeType = computed(() => {
  if (teeTimeType.value === 'hole_9') return BookingTeeTimeType.HOLE_9
  return BookingTeeTimeType.HOLE_18
})

const bookingEvent = ref<GqlBookingEvent | null>(null)
const bookingTeeTime = ref<GqlBookingTeeTime | null>(null)
const bookingAdmissions = ref<BookingAdmission[]>([])
const bookingAttendees = ref<BookingAttendee[]>([])
const bookingSavedCards = ref<GqlSavedCard[]>([])
const bookingCustomAttendeeFields = ref<CustomAttendeeField[]>([])
const selectedPaymentMethods = ref<string[]>(
  user.id ? [] : ['NEW_CARD']
)
const checkoutSecurelyLoading = ref(false)
const inputSelectKey = ref(0)
const guestUserId = ref<string | null>(null)

// --- GUEST & VALIDATION ---
const proceedLabel = computed(() =>
  user.id ? 'Proceed to payment' : 'Proceed to payment as guest'
)
const attendeeErrorTrigger = ref(false)
const attendeesValid = computed(() => {
  if (bookingAttendees.value.length === 0) return false

  return bookingAttendees.value.every((attendee) => {
    // Check basic fields
    if (!attendee.firstName?.trim() || !attendee.lastName?.trim() || !attendee.email.trim()) {
      return false
    }

    // Check required custom fields
    if (bookingCustomAttendeeFields.value.length > 0) {
      for (const field of bookingCustomAttendeeFields.value) {
        if (field.required) {
          // If customFields doesn't exist or the field is missing, it's invalid
          if (!attendee.customFields || !(field.id in attendee.customFields)) {
            return false
          }

          const value = attendee.customFields[field.id]
          
          // For text fields, check if value exists and is not empty
          if (field.type === 'text') {
            if (!value || (typeof value === 'string' && !value.trim())) {
              return false
            }
          } else if (field.type === 'number') {
            // Number fields: check if value is undefined, null, empty string, or 0
            // For required fields, we treat 0 as "not filled" since most use cases
            // (shirt size, handicap, etc.) won't be 0. If 0 is needed, make field optional.
            if (value === undefined || value === null || value === '' || value === '0') {
              return false
            }
          }
        }
      }
    }

    return true
  })
})
function handleProceedToPayment() {
  // Always validate attendees, regardless of login status
  if (attendeesValid.value) {
    attendeeErrorTrigger.value = false
    currentStep.value = NewBookingStep.Payment
  } else {
    attendeeErrorTrigger.value = true
  }
}

// --- FORMATTING ---
const bookingAttendeesFormatted = computed<BookingAttendeeFormatted[]>(() => {
  const admissionIdProperty =
    bookingType.value === BookingType.EVENT
      ? 'golfEventAdmissionId'
      : 'teeTimeAdmissionId'

  return bookingAttendees.value.map((attendee) => {
    const { id, firstName, lastName, email, customFields } = attendee

    return {
      [admissionIdProperty]: id,
      firstName,
      lastName,
      email,
      customFields: customFields ? JSON.stringify(customFields) : undefined
    }
  })
})

const getCheckoutAdmissionSortPrice = (admission: BookingAdmission): number => {
  if (bookingType.value === BookingType.TEE_TIME) {
    if (bookingTeeTimeType.value === BookingTeeTimeType.HOLE_9) {
      return admission.fee9 ?? admission.fee18 ?? 0
    }
    return admission.fee18 ?? admission.fee9 ?? 0
  }
  return admission.price ?? 0
}

const compareCheckoutAdmissions = (
  a: BookingAdmission,
  b: BookingAdmission
): number => {
  const aSingle = (a.attendees ?? 1) === 1 ? 0 : 1
  const bSingle = (b.attendees ?? 1) === 1 ? 0 : 1
  if (aSingle !== bSingle) return aSingle - bSingle

  const priceDiff =
    getCheckoutAdmissionSortPrice(a) - getCheckoutAdmissionSortPrice(b)
  if (priceDiff !== 0) return priceDiff

  return a.admissionTypePosition - b.admissionTypePosition
}

const bookingAdmissionOptions = () => {
  return [...bookingAdmissions.value]
    .sort(compareCheckoutAdmissions)
    .map((admission) => ({
      name: admission.id,
      label: admission.admissionTypeName,
      value: admission.id,
      attendees: admission.attendees
    }))
}

const savedCardsOptions = computed(() => {
  const options: { name: string; label: string; value: string }[] = []

  options.push({
    name: 'NEW_CARD',
    label: 'Add a new card',
    value: 'NEW_CARD'
  })

  bookingSavedCards.value.forEach((card) => {
    options.push({
      name: card.stripeCardId,
      label: `${card.brand?.toUpperCase()} **** **** **** ${card.last4} (${
        card.expMonth
      }/${card.expYear?.toString().slice(-2)})`,
      value: card.stripeCardId
    })
  })

  return options
})

const selectedPaymentMethod = computed(() => {
  const paymentMethod = selectedPaymentMethods.value[0] || ''
  return paymentMethod
})

// --- FETCH FUNCTIONS ---
const fetchBookingEvent = async () => {
  try {
    const { data: getBookingEventData } = await client.query<getBookingEvent>({
      query: GET_BOOKING_EVENT_QUERY,
      variables: {
        id: id.value
      }
    })

    if (getBookingEventData.getEvent.__typename === 'GetEventOutput') {
      bookingEvent.value = getBookingEventData.getEvent.event

      const admissions = getBookingEventData.getEvent.event?.admissions

      if (admissions && admissions.length) {
        bookingAdmissions.value = admissions.map((admission) => {
          return {
            id: admission.id,
            price: admission.price,
            admissionTypeId:
              admission.admissionType.__typename === 'GetAdmissionTypeOutput'
                ? admission.admissionType.admissionType.id
                : '',
            admissionTypeName:
              admission.admissionType.__typename === 'GetAdmissionTypeOutput'
                ? admission.admissionType.admissionType.name
                : '',
            admissionTypePosition:
              admission.admissionType.__typename === 'GetAdmissionTypeOutput'
                ? admission.admissionType.admissionType.position
                : 0,
            attendees:
              admission.admissionType.__typename === 'GetAdmissionTypeOutput'
                ? admission.admissionType.admissionType.attendees ?? 1
                : 1
          }
        })
      }

      // Extract custom attendee fields
      const customFields = getBookingEventData.getEvent.event?.eventCustomAttendeeFields
      if (customFields && customFields.length) {
        bookingCustomAttendeeFields.value = customFields
          .map((field) => {
            if (
              field.customAttendeeField.__typename === 'GetCustomAttendeeFieldOutput' &&
              field.customAttendeeField.customAttendeeField
            ) {
              return {
                id: field.id, // Use EventCustomAttendeeField.id instead of CustomAttendeeField.id
                label: field.customAttendeeField.customAttendeeField.label,
                type: field.customAttendeeField.customAttendeeField.type as 'text' | 'number',
                required: field.customAttendeeField.customAttendeeField.required
              }
            }
            return null
          })
          .filter((field): field is CustomAttendeeField => field !== null)
          .sort((a, b) => {
            // Sort by position - now we can use the field.id directly to find position
            const aPos = customFields.find((f) => f.id === a.id)?.position ?? 0
            const bPos = customFields.find((f) => f.id === b.id)?.position ?? 0
            return aPos - bPos
          })
      } else {
        bookingCustomAttendeeFields.value = []
      }
    }
  } catch (e) {
    console.error(e)
    toastsStore.pushErrorToast('Error fetching event.')
  }
}

const fetchBookingTeeTime = async () => {
  try {
    const { data: getBookingTeeTimeData } =
      await client.query<getBookingTeeTime>({
        query: GET_BOOKING_TEE_TIME_QUERY,
        variables: {
          id: id.value
        }
      })

    if (getBookingTeeTimeData.GetTeeTime.__typename === 'GetTeeTimeOutput') {
      bookingTeeTime.value = getBookingTeeTimeData.GetTeeTime.teeTime

      const admissions = getBookingTeeTimeData.GetTeeTime.teeTime?.admissions

      if (admissions && admissions.length) {
        bookingAdmissions.value = admissions.map((admission) => {
          return {
            id: admission.id,
            fee9: admission.fee9,
            fee18: admission.fee18,
            admissionTypeId:
              admission.admissionType.__typename === 'GetAdmissionTypeOutput'
                ? admission.admissionType.admissionType.id
                : '',
            admissionTypeName:
              admission.admissionType.__typename === 'GetAdmissionTypeOutput'
                ? admission.admissionType.admissionType.name
                : '',
            admissionTypePosition:
              admission.admissionType.__typename === 'GetAdmissionTypeOutput'
                ? admission.admissionType.admissionType.position
                : 0
          }
        })
      }
    }
  } catch (e) {
    console.error(e)
    toastsStore.pushErrorToast('Error fetching tee time.')
  }
}

const fetchSavedCards = async () => {
  try {
    const { data } = await client.query<getBookingSavedCards>({
      query: GET_BOOKING_SAVED_CARDS_QUERY,
      variables: {
        userId: user.id
      },
      fetchPolicy: 'network-only'
    })

    if (data.getSavedCards.__typename === 'GetSavedCardsOutput') {
      bookingSavedCards.value = data.getSavedCards.savedCards || []
    }
  } catch (e) {
    console.error(e)
    toastsStore.pushErrorToast('Error retrieving saved payment methods.')
  }
}

const parseBookingAdmissions = () => {
  bookingAdmissions.value = []

  if (bookingType.value === BookingType.EVENT && bookingEvent.value) {
    const admissions = bookingEvent.value.admissions

    if (admissions?.length) {
      admissions?.forEach((admission) => {
        if (admission.admissionType.__typename === 'GetAdmissionTypeOutput') {
          bookingAdmissions.value.push({
            id: admission.id,
            price: admission.price,
            admissionTypeId: admission.admissionType.admissionType.id,
            admissionTypeName: admission.admissionType.admissionType.name,
            admissionTypePosition:
              admission.admissionType.admissionType.position,
            attendees: admission.admissionType.admissionType.attendees ?? 1
          })
        }
      })
    }
  } else if (bookingType.value === BookingType.EVENT && bookingTeeTime.value) {
    const admissions = bookingTeeTime.value.admissions

    if (admissions?.length) {
      admissions?.forEach((admission) => {
        if (admission.admissionType.__typename === 'GetAdmissionTypeOutput') {
          bookingAdmissions.value.push({
            id: admission.id,
            fee9: admission.fee9,
            fee18: admission.fee18,
            admissionTypeId: admission.admissionType.admissionType.id,
            admissionTypeName: admission.admissionType.admissionType.name,
            admissionTypePosition:
              admission.admissionType.admissionType.position,
            attendees: 1
          })
        }
      })
    }
  }
}

const onUpdateAttendeesForm = (attendees: BookingAttendee[]) => {
  bookingAttendees.value = [...attendees]
}

const bookingInformation = computed(() => {
  let bookingInformation: EventTeeTimeType = {
    type: 'event',
    clubName: '',
    clubAddress1: '',
    clubAddress2: '',
    date: formatDate(new Date()),
    dateDescription: '',
    time: '00:00 AM',
    timeDescription: '',
    eventName: ''
  }

  if (bookingType.value === BookingType.EVENT && bookingEvent.value) {
    const type = 'event'
    const clubName =
      bookingEvent.value.club.__typename === 'GetClubOutput'
        ? bookingEvent.value.club.club?.clubName
        : ''
    const clubAddress1 =
      bookingEvent.value.club.__typename === 'GetClubOutput'
        ? bookingEvent.value.club.club?.addressLine1
        : ''
    const clubAddress2 =
      bookingEvent.value.club.__typename === 'GetClubOutput'
        ? bookingEvent.value.club.club?.addressLine2
        : ''
    const dateStart = new Date(bookingEvent.value.dateStart)
    const date = formatDate(dateStart)
    const dateDescription = bookingEvent.value.startDateDescription
    const time = bookingEvent.value.startTime
    const timeDescription = bookingEvent.value.startTimeDescription
    const eventName = bookingEvent.value.name

    bookingInformation = {
      type,
      clubName: clubName || '',
      clubAddress1: clubAddress1 || '',
      clubAddress2: clubAddress2 || '',
      date,
      dateDescription: dateDescription || '',
      time: time || '',
      timeDescription: timeDescription || '',
      eventName
    }
  } else if (
    bookingType.value === BookingType.TEE_TIME &&
    bookingTeeTime.value
  ) {
    const type = 'tee_time'

    let holes = 18
    if (bookingTeeTimeType.value === BookingTeeTimeType.HOLE_9) {
      holes = 9
    }

    let course = ''
    if (bookingTeeTime.value.course.__typename === 'GetCourseOutput') {
      course = bookingTeeTime.value.course.course?.courseName || ''
    }

    let clubName = ''
    let clubAddress1 = ''
    let clubAddress2 = ''
    if (
      bookingTeeTime.value.course.__typename === 'GetCourseOutput' &&
      bookingTeeTime.value.course.course?.club.__typename === 'GetClubOutput'
    ) {
      clubName = bookingTeeTime.value.course.course.club.club?.clubName || ''
      clubAddress1 =
        bookingTeeTime.value.course.course.club.club?.addressLine1 || ''
      clubAddress2 =
        bookingTeeTime.value.course.course.club.club?.addressLine2 || ''
    }

    const dateType = new Date(bookingTeeTime.value.time)
    const date = formatDate(dateType)
    const time = dateType.toLocaleString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    })

    bookingInformation = {
      type,
      clubName,
      clubAddress1,
      clubAddress2,
      date,
      dateDescription: '',
      time,
      timeDescription: '',
      course,
      holes
    }
  }

  return bookingInformation
})

const attendeesInformation = computed(() => {
  return bookingAttendees.value.map((attendee) => ({
    name: `${attendee.firstName} ${attendee.lastName}`,
    email: attendee.email
  }))
})

const purchaseInformation = computed(() => {
  const items: { name: string; price: number }[] = []

  if (bookingType.value === BookingType.EVENT) {
    // Group by admission (ticket type): one line per ticket, in same order as form
    const byAdmissionId = new Map<string, number>()
    const admissionIdOrder: string[] = []
    for (const attendee of bookingAttendees.value) {
      const admission = bookingAdmissions.value.find((a) => a.id === attendee.id)
      if (!admission) continue
      const count = byAdmissionId.get(admission.id) ?? 0
      byAdmissionId.set(admission.id, count + 1)
      if (count === 0) admissionIdOrder.push(admission.id)
    }
    const admissionById = new Map(
      bookingAdmissions.value.map((a) => [a.id, a])
    )
    for (const admissionId of admissionIdOrder) {
      const admission = admissionById.get(admissionId)
      if (!admission) continue
      const attendeeCount = byAdmissionId.get(admissionId) ?? 0
      if (attendeeCount === 0) continue
      const attendeesPerTicket = admission.attendees ?? 1
      const ticketCount = attendeeCount / attendeesPerTicket
      let ticketPrice = admission.price ?? 0
      if (user.isPremium && bookingEvent.value?.memberDiscount) {
        const memberDiscount = bookingEvent.value.memberDiscount / 100
        ticketPrice = ticketPrice * (1 - memberDiscount)
      }
      const lineTotal = ticketCount * ticketPrice
      items.push({
        name: admission.admissionTypeName,
        price: lineTotal
      })
    }
  } else {
    // Tee time: one line per attendee (existing behaviour)
    bookingAttendees.value.forEach((attendee) => {
      const admission = bookingAdmissions.value.find(
        (admission) => admission.id === attendee.id
      )
      const name = `${attendee.firstName} ${attendee.lastName}`.trim()
      let price = 0
      if (admission) {
        price = admission.price || price
        if (bookingTeeTimeType.value === BookingTeeTimeType.HOLE_9)
          price = admission.fee9 || price
        else price = admission.fee18 || price
      }
      if (name.length) {
        items.push({ name, price })
      }
    })
  }

  const subtotal = items.reduce((sum, item) => sum + item.price, 0)
  // Check if event is GST exempt
  const isGstExempt = bookingType.value === BookingType.EVENT && bookingEvent.value?.gstExempt === true
  const tax = isGstExempt ? 0 : subtotal * 0.05
  const total = subtotal + tax
  const points = user.isPremium ? Math.floor(subtotal) : undefined

  return {
    items,
    tax,
    total,
    points
  }
})

const fetchData = async () => {
  if (bookingType.value === BookingType.EVENT) {
    await fetchBookingEvent()
  } else if (bookingType.value === BookingType.TEE_TIME) {
    await fetchBookingTeeTime()
  }

  parseBookingAdmissions()

  await fetchSavedCards()
}

const validateData = () => {
  if (
    (bookingType.value === BookingType.EVENT && !bookingEvent.value) ||
    (bookingType.value === BookingType.TEE_TIME && !bookingTeeTime.value)
  ) {
    toastsStore.pushErrorToast('Invalid booking parameters.')
  }
}

const isLoading = ref<boolean>(false)

const handleNewBooking = async () => {
  if (isLoading.value) return

  try {
    isLoading.value = true

    // Build the common vars
    const common = {
      bookingType:       bookingType.value,
      attendees:         bookingAttendeesFormatted.value,
      teeTimeId:         bookingType.value === BookingType.TEE_TIME ? id.value : undefined,
      teeTimeType:       bookingType.value === BookingType.TEE_TIME ? bookingTeeTimeType.value : undefined,
      eventId:           bookingType.value === BookingType.EVENT   ? id.value : undefined,
      paymentMethodId:   selectedPaymentMethod.value || undefined
    }

    if (user.id) {
      // logged‐in path
      const { data } = await client.mutate<newBooking>({
        mutation: NEW_BOOKING_MUTATION,
        variables: {
          ...common,
          primaryBookerId: user.id
        }
      })

      if (data?.newBooking.__typename === 'NewBookingOutput') {
        if (data.newBooking.full) {
          const label = bookingType.value === BookingType.EVENT ? 'Event' : 'Tee time'
          toastsStore.pushErrorToast(`${label} is full.`)
        } else if (data.newBooking.booking) {
          toastsStore.pushToast({
            icon: 'check-circle',
            content: 'Booking successful!',
            semantic: ToastSemantic.Success
          })
          router.replace({ path: '/booking/confirmation', query: { id: data.newBooking.booking.id } })
        }
      } else if (data?.newBooking.__typename === 'PaymentAccountNotReadyError') {
        toastsStore.pushErrorToast('Unexpected error: Event Account.')
      } else {
        toastsStore.pushErrorToast('Unexpected error. Please try again later.')
      }
    } else {
      // guest path
      if (!guestUserId.value) {
        toastsStore.pushErrorToast('Something went wrong—please reload and try again.')
      } else {
        const { data } = await client.mutate<newGuestBooking>({
          mutation: NEW_GUEST_BOOKING_MUTATION,
          variables: {
            ...common,
            primaryGuestId: guestUserId.value
          }
        })

        if (data?.newBooking.__typename === 'NewBookingOutput') {
          if (data.newBooking.full) {
            const label = bookingType.value === BookingType.EVENT ? 'Event' : 'Tee time'
            toastsStore.pushErrorToast(`${label} is full.`)
          } else if (data.newBooking.booking) {
            toastsStore.pushToast({
              icon: 'check-circle',
              content: 'Booking successful!',
              semantic: ToastSemantic.Success
            })
            router.replace({ path: '/booking/confirmation', query: { id: data.newBooking.booking.id } })
          }
        } else if (data?.newBooking.__typename === 'PaymentAccountNotReadyError') {
          toastsStore.pushErrorToast('Unexpected error: Event Account.')
        } else {
          toastsStore.pushErrorToast('Unexpected error. Please try again later.')
        }
      }
    }
  } catch (e) {
    console.error(e)
    toastsStore.pushErrorToast('Unexpected error. Please try again later.')
  } finally {
    isLoading.value = false
  }
}


const stripeFormState = useViewState({})

const { request: setupRequest } = useViewRequest<newSetupIntent>(
  {
    type: 'mutation',
    request: NEW_SETUP_INTENT_MUTATION,
    variableConfig: { userId: () => user.id }
  },
  stripeFormState
)
const { request: guestSetupRequest } = useViewRequest<newGuestSetupIntent>(
  {
    type: 'mutation',
    request: NEW_GUEST_SETUP_INTENT_MUTATION,
    // here we tell it how to build the `$input` var at call-time:
    variableConfig: {
      input: () => {
        const first = bookingAttendees.value[0] || { email: '', firstName: '', lastName: '' }
        return {
          email:     first.email,
          firstName: first.firstName || '',
          lastName:  first.lastName  || ''
        }
      }
    }
  },
  stripeFormState
)

const stripeElementCard = ref<HTMLElement>()
const stripeElementLoading = ref(false)
const stripeElementReady = ref(false)
let stripe, elements, paymentCard

// Change mount function to accept dynamic clientSecret
const mountStripeElementCard = (clientSecret: string) => {
  ;[stripe, elements] = stripeElements(
    clientSecret,
    import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY
  )
  paymentCard = elements.create('payment')
  paymentCard.mount(stripeElementCard.value)
  stripeElementReady.value = true
}

const handleInlineNewCard = async () => {
  if (stripeElementLoading.value || stripeElementReady.value) return
  stripeElementLoading.value = true
  await nextTick()
  if (!stripeElementCard.value) {
    console.error('Stripe element container is not available after nextTick.')
    stripeElementLoading.value = false
    return
  }

  if (user.id) {
    // logged‐in path
    await setupRequest({
      success: async (data) => {
        if (
          data.newSetupIntent?.__typename === 'NewSetupIntentOutput' &&
          data.newSetupIntent.clientSecret
        ) {
          mountStripeElementCard(data.newSetupIntent.clientSecret)
          return true
        }
      }
    })
  } else {
    // guest path: use first attendee’s info
    await guestSetupRequest({
      success: async (data) => {
        if (
          data.newGuestSetupIntent?.__typename === 'NewGuestSetupIntentOutput' &&
          data.newGuestSetupIntent.clientSecret
        ) {
          guestUserId.value = data.newGuestSetupIntent.guestUserId
          mountStripeElementCard(data.newGuestSetupIntent.clientSecret)
          return true
        }
      }
    })
  }

  stripeElementLoading.value = false
}

const handleCheckoutSecurelyButton = async () => {
  if (checkoutSecurelyLoading.value) return
  checkoutSecurelyLoading.value = true

  // If using a new card, validate the card details via Stripe
  if (selectedPaymentMethod.value === 'NEW_CARD') {
    const { error, setupIntent } = await submitCardForValidation()
    if (error) {
      toastsStore.pushErrorToast(
        'Invalid card details. Please check your card information and try again.'
      )
      checkoutSecurelyLoading.value = false
      return
    }

    if (setupIntent && setupIntent.payment_method) {
      if (user.id) {
        await fetchSavedCards()
        const newCard = bookingSavedCards.value.find(
          (card) => card.stripeCardId === setupIntent.payment_method
        )
        if (newCard) {
          selectedPaymentMethods.value = [newCard.stripeCardId]
          inputSelectKey.value++
        }
      } else {
        // for guests just use the one-off payment_method
        selectedPaymentMethods.value = [setupIntent.payment_method]
      }
    }
  }
  // Advance to the next step (for example, Confirm Booking)
  currentStep.value = nextStep(currentStep.value) || currentStep.value
  checkoutSecurelyLoading.value = false
}

const submitCardForValidation = async () => {
  if (!(stripe && elements)) {
    return { error: 'Stripe has not been initialized.' }
  }

  // Call stripe.confirmSetup to validate the details entered in the card Element.
  const { error, setupIntent } = await stripe.confirmSetup({
    elements,
    confirmParams: {
      return_url: window.location.href
    },
    redirect: 'if_required'
  })

  return { error, setupIntent }
}

watch(selectedPaymentMethod, (newVal) => {
  if (newVal === 'NEW_CARD') {
    nextTick(() => {
      handleInlineNewCard()
    })
  }
})

// auto‐mount Stripe for guests on hitting the Payment step
watch(currentStep, (step) => {
  if (
    step === NewBookingStep.Payment &&
    !user.id &&
    !stripeElementReady.value
  ) {
    nextTick(() => {
      handleInlineNewCard()
    })
  }
})


// Standard lifecycle hooks
onMounted(() => {
  validateData()
})
onBeforeRouteUpdate(async () => {
  await fetchData()
  validateData()
})
await fetchData()
</script>

<template>
  <div class="booking-new">
    <div class="grid__row">
      <div class="grid__column grid__column--12">
        <USButton
          v-if="previousStep(currentStep)"
          type="inline"
          icon="arrow-left"
          :label="`Back to ${previousStep(currentStep)}`"
          class="margin-bottom--6"
          @click="currentStep = previousStep(currentStep)"
        />
        <h2 class="heading--h2 margin-bottom--6">Complete Your Booking</h2>
      </div>
    </div>
    <SplitContent align-items="flex-start">
      <template #left>
        <div class="booking-new__left">
          <h3 class="heading--h3 margin-bottom--6">Booking Overview</h3>
          <InformationCardEventTeeTime
            :event="bookingInformation"
            :players="bookingAttendees.length"
          />
          <InformationCardPlayers
            v-if="attendeesInformation.length"
            class="margin-top--6"
            :players="attendeesInformation"
            :read-only="currentStep === NewBookingStep.Attendees"
            @edit="currentStep = NewBookingStep.Attendees"
          />
          <InformationCardOrderTotal
            v-if="attendeesInformation.length"
            class="margin-top--6"
            :order="purchaseInformation"
          />
        </div>
      </template>
      <template #right>
        <div class="booking-new__right">
          <div v-show="currentStep === NewBookingStep.Attendees">
            <BookingAttendeesForm
              :admissions="bookingAdmissionOptions()"
              :show-errors="attendeeErrorTrigger"
              :custom-fields="bookingCustomAttendeeFields"
              @update="onUpdateAttendeesForm"
            />
            <div class="grid__row">
              <div class="grid__column grid__column--12">
                <USButton
                  :label="proceedLabel"
                  class="margin-top--5"
                  :disabled="!attendeesValid"
                  @click="handleProceedToPayment"
                />
                <USButton
                  v-if="!user.id"
                  label="Login"
                  type="inline"
                  class="margin-top--5 margin-left--4"
                  @click="router.push({ name: usersRouteNames.SignIn })"
                />
              </div>
            </div>
          </div>
          <div v-show="currentStep === NewBookingStep.Payment">
            <h3 class="heading--h3 margin-bottom--6">Payment Details</h3>
            <template v-if="user.id">
              <p class="display--d2--semibold margin-bottom--9">Payment Method</p>
              <p class="body--p1--regular margin-bottom--6">
                Please select one of the saved payment options below.
              </p>
              <USInputSelect
                :key="inputSelectKey"
                v-model="selectedPaymentMethods"
                :options="savedCardsOptions"
                heading="Saved Cards"
              />
              <div v-if="selectedPaymentMethod === 'NEW_CARD'">
                <div class="divider"><span>Add a new card</span></div>
                <div class="booking-new__stripe-card-wrapper">
                  <div id="stripe-element--card" ref="stripeElementCard" />
                </div>
              </div>
            </template>
            <template v-else>
              <div class="booking-new__stripe-card-wrapper">
                <div id="stripe-element--card" ref="stripeElementCard" />
              </div>
            </template>
            <USButton
              class="margin-top--5 margin-right--6"
              type="inline"
              icon="arrow-left"
              label="Back"
              @click="currentStep = previousStep(currentStep)"
            />
            <USButton
              class="margin-top--5"
              icon="lock"
              label="Checkout Securely"
              :disabled="checkoutSecurelyLoading || !selectedPaymentMethod"
              @click="handleCheckoutSecurelyButton"
            />
          </div>
          <div v-show="currentStep === NewBookingStep.ConfirmBooking">
            <h3 class="heading--h3 margin-bottom--8">Confirmation</h3>
            <p class="body--p1--regular">
              Please confirm that the information provided is accurate. Once
              confirmed, please click "Complete Booking" to finish your order.
            </p>
            <USButton
              class="margin-top--5 margin-right--6"
              type="inline"
              icon="arrow-left"
              label="Back"
              @click="currentStep = previousStep(currentStep)"
            />
            <USButton
              class="margin-top--5"
              label="Complete Booking"
              :disabled="isLoading"
              @click="handleNewBooking"
            />
          </div>
        </div>
      </template>
    </SplitContent>
  </div>
</template>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint';
.booking-new {
  min-height: 100vh;

  &__right {
    margin-top: var(--spacing--1);

    @include breakpoint.min-width(breakpoint.$breakpoint--tablet) {
      margin-top: 0;
    }
  }
}

.divider {
  display: flex;
  align-items: center;
  text-align: center;
  margin: var(--spacing--5) 0;
}

.divider::before,
.divider::after {
  content: "";
  flex: 1;
  border-bottom: 1px solid var(--color--neutral-a4);
}

.divider::before {
  margin-right: 10px;
}

.divider::after {
  margin-left: 10px;
}
</style>
